import { getCategoryIcons } from "app/services/api/category";
import jwtAuthKioskAxios from "app/services/auth/jwtAuthKiosk";
import { useEffect, useState } from "react";
import { Box, Modal, Typography, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

const CategoryIcon = ({ name, setFieldValue, value }) => {
  const { t } = useTranslation();
  const [icons, setIcons] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    getCategoryIcons().then((icons) => {
      setIcons(icons);
    });
  }, []);

  const toSvgDataUrl = (icon) => {
    if (!icon) return "";
    // Icon may be an object with base64 or svg content
    const payload =
      typeof icon === "object"
        ? icon.base64 || icon.svg || icon.data || ""
        : icon;
    if (!payload) return "";
    // Already a full data url
    if (typeof payload === "string" && payload.startsWith("data:"))
      return payload;
    // If it's raw SVG XML, convert to data URL
    if (typeof payload === "string" && payload.trim().startsWith("<svg")) {
      const encoded = btoa(unescape(encodeURIComponent(payload)));
      return `data:image/svg+xml;base64,${encoded}`;
    }
    // Otherwise assume base64 payload
    return `data:image/svg+xml;base64,${payload}`;
  };

  const toBackendSvgUrl = (icon) => {
    const base = (jwtAuthKioskAxios?.defaults?.baseURL || "").replace(
      /\/+$/,
      ""
    );
    const join = (p) => {
      if (!p) return "";
      if (typeof p !== "string") return "";
      if (p.startsWith("data:")) return p;
      if (/^https?:\/\//i.test(p)) return p;
      if (p.startsWith("/")) return `${base}${p}`;
      return base ? `${base}/${p}` : p;
    };
    if (!icon) return "";
    if (typeof icon === "string") {
      // URL/path provided
      if (icon.trim().startsWith("<svg")) {
        return toSvgDataUrl(icon);
      }
      return join(icon);
    }
    if (typeof icon === "object") {
      const urlLike = icon.url || icon.path || icon.filePath || icon.src;
      if (urlLike) return join(urlLike);
      const key = icon.key || icon.name || icon.fileName || icon.filename;
      if (key && typeof key === "string" && !key.trim().startsWith("<svg")) {
        // Try a conventional backend path for icons by key/name
        return join(`/api/v1/categories/icons/file/${encodeURIComponent(key)}`);
      }
      // Fallback to embedded data
      const embedded = icon.base64 || icon.svg || icon.data;
      if (embedded) return toSvgDataUrl(embedded);
    }
    return "";
  };

  const getIconName = (icon) => {
    // Try common name fields on object payloads
    if (icon && typeof icon === "object") {
      const raw = icon.name || icon.fileName || icon.filename || icon.key || "";
      if (raw) return String(raw).replace(/\.[a-zA-Z0-9]+$/, "");
    }
    // If string looks like a URL or path, extract filename
    if (typeof icon === "string") {
      try {
        const withoutQuery = icon.split("?")[0];
        const parts = withoutQuery.split("/");
        const last = parts[parts.length - 1] || "";
        if (last) return last.replace(/\.[a-zA-Z0-9]+$/, "");
      } catch (_) {
        // ignore
      }
    }
    return "";
  };

  return (
    <div>
      {!value ? (
        <Box
          onClick={() => setOpen(true)}
          sx={{
            width: 48,
            height: 48,
            border: "1px dashed #999",
            borderRadius: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#999",
            cursor: "pointer",
            backgroundColor: "#fafafa",
          }}
          title="Select icon"
        >
          +
        </Box>
      ) : (
        <Box sx={{ display: "inline-flex", position: "relative" }}>
          <img
            onClick={() => setOpen(true)}
            src={toBackendSvgUrl(value) || toSvgDataUrl(value)}
            alt="Category Icon"
            style={{
              width: 48,
              height: 48,
              borderRadius: 6,
              background: "#fff",
              cursor: "pointer",
            }}
            title="Change icon"
          />
          <Box
            onClick={() => setFieldValue(name, "")}
            sx={{
              position: "absolute",
              top: -6,
              right: -6,
              width: 20,
              height: 20,
              borderRadius: "50%",
              bgcolor: "#fff",
              border: "1px solid #ccc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              lineHeight: 1,
              cursor: "pointer",
              boxShadow: 1,
            }}
            title="Remove icon"
          >
            ×
          </Box>
        </Box>
      )}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "70vw",
            maxHeight: "70vh",
            overflow: "auto",
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 24,
            p: 2,
          }}
        >
          {(() => {
            const customIcons = (icons || []).filter((i) =>
              typeof i === "object" ? i.type === "custom" : false
            );
            const systemIcons = (icons || []).filter(
              (i) => !(typeof i === "object" && i.type === "custom")
            );

            return (
              <Box>
                {customIcons.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t("pages.dsokCategories.customIcons")}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", mb: 2 }}>
                      {customIcons.map((icon, index) => {
                        const iconValue =
                          icon?.url ||
                          icon?.path ||
                          icon?.filePath ||
                          icon?.src ||
                          icon?.base64 ||
                          icon?.svg ||
                          icon;
                        const src = toBackendSvgUrl(icon) || toSvgDataUrl(icon);
                        const isSelected = iconValue === value;
                        return (
                          <img
                            key={`custom-${index}`}
                            src={src}
                            alt={`Custom Icon ${index}`}
                            onClick={() => {
                              setFieldValue(name, iconValue);
                              setOpen(false);
                            }}
                            style={{
                              width: 48,
                              height: 48,
                              border: isSelected
                                ? "2px solid #1976d2"
                                : "1px solid #ccc",
                              borderRadius: 6,
                              padding: 4,
                              cursor: "pointer",
                              margin: 6,
                              background: "#fff",
                            }}
                          />
                        );
                      })}
                    </Box>
                  </>
                )}

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("pages.dsokCategories.systemIcons")}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                  {systemIcons.map((icon, index) => {
                    const iconValue =
                      icon?.url ||
                      icon?.path ||
                      icon?.filePath ||
                      icon?.src ||
                      icon?.base64 ||
                      icon?.svg ||
                      icon;
                    const src = toBackendSvgUrl(icon) || toSvgDataUrl(icon);
                    const isSelected = iconValue === value;
                    return (
                      <img
                        key={`sys-${index}`}
                        src={src}
                        alt={`System Icon ${index}`}
                        onClick={() => {
                          setFieldValue(name, iconValue);
                          setOpen(false);
                        }}
                        style={{
                          width: 48,
                          height: 48,
                          border: isSelected
                            ? "2px solid #1976d2"
                            : "1px solid #ccc",
                          borderRadius: 6,
                          padding: 4,
                          cursor: "pointer",
                          margin: 6,
                          background: "#fff",
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Modal>
    </div>
  );
};

export default CategoryIcon;
